import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PreviewTimeline } from '../animation/previewSim'

/**
 * rAF-driven playback clock for the animated switch-list preview
 * (Plan 26 Phase 1d). Timeline progress is measured in virtual feet so
 * scrubbing and stepping share one coordinate space with `snapshotAt`.
 *
 * Default pace ≈ 150 ft/s (a slow switch move); speed multipliers apply.
 */

/** Virtual playback pace at 1× — used by the transport bar to show time. */
export const DEFAULT_SPEED_FPS = 150
export const PREVIEW_SPEEDS = [0.5, 1, 2, 4] as const

export interface PreviewPlayback {
  feet: number
  totalFeet: number
  playing: boolean
  done: boolean
  speedMult: number
  /** index of the timeline step containing `feet`. */
  play: () => void
  pause: () => void
  toggle: () => void
  restart: () => void
  seek: (feet: number) => void
  /** Jump to the boundary of the next (dir=1) or previous (dir=-1) step. */
  stepBoundary: (dir: 1 | -1) => void
  setSpeedMult: (m: number) => void
}

export const usePreviewPlayback = (
  timeline: PreviewTimeline | null
): PreviewPlayback => {
  const [feet, setFeet] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speedMult, setSpeedMult] = useState<number>(1)
  const totalFeet = timeline?.totalFeet ?? 0
  const rafRef = useRef<number | null>(null)
  const lastTsRef = useRef<number | null>(null)

  const done = totalFeet > 0 && feet >= totalFeet

  useEffect(() => {
    if (!playing) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = null
      return
    }
    const tick = (ts: number) => {
      const last = lastTsRef.current ?? ts
      const dt = Math.min((ts - last) / 1000, 0.1) // clamp tab-switch jumps
      lastTsRef.current = ts
      setFeet(prev => {
        const next = prev + dt * DEFAULT_SPEED_FPS * speedMult
        if (next >= totalFeet) {
          setPlaying(false)
          return totalFeet
        }
        return next
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = null
    }
  }, [playing, speedMult, totalFeet])

  const play = useCallback(() => {
    if (done) setFeet(0)
    setPlaying(true)
  }, [done])
  const pause = useCallback(() => setPlaying(false), [])
  const toggle = useCallback(() => {
    setPlaying(p => {
      if (!p && done) setFeet(0)
      return !p
    })
  }, [done])
  const restart = useCallback(() => {
    setFeet(0)
    setPlaying(true)
  }, [])
  const seek = useCallback(
    (f: number) => setFeet(Math.max(0, Math.min(f, totalFeet))),
    [totalFeet]
  )

  const boundaries = useMemo(
    () => (timeline ? [0, ...timeline.steps.map(s => s.startFeet + s.spanFeet)] : [0]),
    [timeline]
  )
  const stepBoundary = useCallback(
    (dir: 1 | -1) => {
      const target =
        dir === 1
          ? boundaries.find(b => b > feet + 1e-6)
          : [...boundaries].reverse().find(b => b < feet - 1e-6)
      setFeet(Math.max(0, Math.min(target ?? (dir === 1 ? totalFeet : 0), totalFeet)))
      setPlaying(false)
    },
    [boundaries, feet, totalFeet]
  )

  return {
    feet,
    totalFeet,
    playing,
    done,
    speedMult,
    play,
    pause,
    toggle,
    restart,
    seek,
    stepBoundary,
    setSpeedMult,
  }
}
